'use client'
import { useComponentTranslation } from '@core/i18n'
import { Box, Flex, Icon, Tag, Text } from '@core/ui-elements'
import { IPickingProduct } from '@data/types'
import { TFunction } from 'next-i18next'
import { FC, ReactNode, useMemo, useRef, useState } from 'react'
import { IconType } from 'react-icons'
import {
  MdCalendarMonth,
  MdOutlineDocumentScanner,
  MdOutlineInventory2,
} from 'react-icons/md'

import { HiddenBarcodeInput } from '../hidden-barcode-input'
import { LastCharactersStrong } from '../last-characters-strong'
import { LoadingScreen } from '../loading-screen'
import { PickingAmountTags } from '../picking-amount-tags'
import { ProductInformation } from '../product-information'

enum OptionsTags {
  SoonToExpire = 'Soon to Expire',
}

interface TagSchema {
  index: string
  iconColor: string
  backgroundColor: string
  icon: IconType
  label: string
}

const tagSchemaList: Record<OptionsTags, TagSchema> = {
  [OptionsTags.SoonToExpire]: {
    index: '1',
    iconColor: 'yellow.500',
    backgroundColor: 'orange.100',
    icon: MdCalendarMonth,
    label: OptionsTags.SoonToExpire,
  },
}

export interface IProductCard {
  product: IPickingProduct
  actions?: ReactNode
  onScanned: (barcode: string, amount: number) => void
  scannedAmount?: number
  manuallyAmount?: number
  flaggedAmount?: number
  hideLocation?: boolean
  unavailableProduct?: boolean
}

export const ProductCard: FC<IProductCard> = ({
  product,
  onScanned,
  actions,
  scannedAmount = 0,
  manuallyAmount = 0,
  flaggedAmount = 0,
  hideLocation,
  unavailableProduct,
}) => {
  const t = useComponentTranslation('ProductCard') as TFunction
  const cardRef = useRef<HTMLDivElement>(null)

  const [valueBarcode, setValueBarcode] = useState('')

  const calculateBox = useMemo(() => {
    if (product.amount && product.totalAmountPerBox) {
      const boxQuantity = Math.floor(product.amount / product.totalAmountPerBox)
      const units = product.amount % product.totalAmountPerBox

      return {
        boxQuantity,
        units,
      }
    }

    return {
      boxQuantity: 0,
      units: 0,
    }
  }, [product.amount, product.totalAmountPerBox])

  const productCardColor = {
    red: 'red.200',
    blue: 'blue.200',
    green: 'green.200',
    gray: 'gray.200',
  }

  const totalPicked = useMemo(
    () => manuallyAmount + scannedAmount + flaggedAmount,
    [manuallyAmount, scannedAmount, flaggedAmount]
  )

  const getCardColor = useMemo(() => {
    if (unavailableProduct) return productCardColor.red

    if (manuallyAmount && manuallyAmount + scannedAmount === product.amount)
      return productCardColor.blue

    if (scannedAmount === product.amount) return productCardColor.green

    if (flaggedAmount) return productCardColor.red

    return productCardColor.gray
  }, [
    flaggedAmount,
    manuallyAmount,
    product.amount,
    productCardColor.blue,
    productCardColor.gray,
    productCardColor.green,
    productCardColor.red,
    scannedAmount,
    unavailableProduct,
  ])

  if (!product) return <LoadingScreen />

  const productTags = product?.tags

  const showTags = productTags?.some((tag) => tagSchemaList[tag as OptionsTags])

  return (
    <Flex
      ref={cardRef}
      backgroundColor="white"
      borderRadius={'0.5rem'}
      boxShadow="md"
      flexDirection={'column'}
      gap={'0.25rem'}
      width={'100%'}
    >
      {showTags && (
        <Flex
          flexWrap="wrap"
          gap="0.25rem"
          justifyContent="center"
          padding="0.5rem"
        >
          {productTags?.map((tag) => {
            const tagSchema = tagSchemaList[tag as OptionsTags]
            if (tagSchema) {
              const {
                icon: IconComponent,
                iconColor,
                backgroundColor,
                label,
                index,
              } = tagSchema
              return (
                <Tag key={index} background={backgroundColor} size="md">
                  <Icon as={IconComponent} color={iconColor} />
                  {t(label)}
                </Tag>
              )
            }
          })}
        </Flex>
      )}

      <Flex flexDirection={'column'} gap={'0.25rem'} padding="0.5rem">
        <ProductInformation hideLocation={hideLocation} product={product} />
        <Box>
          <Text
            color="gray.500"
            fontSize={'small'}
            lineHeight="0.8rem"
            textAlign={'center'}
          >
            {t('Scanner')}{' '}
            <LastCharactersStrong
              color="var(--chakra-colors-blue-500)"
              label={valueBarcode}
            />
          </Text>
          {!!product.totalAmountPerBox && (
            <Flex alignItems="center" gap="0.75rem" justifyContent="center">
              <Flex alignItems="center" gap="0.25rem">
                <Icon
                  as={MdOutlineDocumentScanner}
                  color="blue.500"
                  fontSize="1rem"
                />
                <Text fontSize="small" fontWeight="600">
                  {t('boxes', { count: calculateBox.boxQuantity })} +
                  {' ' + t('units', { count: calculateBox.units })}
                </Text>
              </Flex>
              <Flex alignItems="center" gap="0.25rem">
                <Icon
                  as={MdOutlineInventory2}
                  color="blue.500"
                  fontSize="1rem"
                />
                <Text alignItems="center" fontSize="small" fontWeight="600">
                  {t('units per box', { count: product.totalAmountPerBox })}
                </Text>
              </Flex>
            </Flex>
          )}
        </Box>

        <HiddenBarcodeInput
          disabled={scannedAmount == product.amount}
          handleSubmit={(barcode) => onScanned(barcode, 1)}
          returnInput={(barcode) => setValueBarcode(barcode)}
        />
        {actions || null}
      </Flex>
      <Flex
        backgroundColor={getCardColor}
        borderBottomRadius="0.5rem"
        gap={'0.25rem'}
        justifyContent={'center'}
        padding={'0.5rem'}
        wrap={'wrap'}
      >
        <PickingAmountTags
          amount={product.amount}
          amountWithColor={totalPicked !== product.amount}
          flaggedAmount={flaggedAmount}
          isCompleted={totalPicked === product.amount}
          manuallyAmount={manuallyAmount}
          scannedAmount={scannedAmount}
          size="lg"
          unavailableProduct={unavailableProduct}
        />
      </Flex>
    </Flex>
  )
}
